import React, { useState } from 'react';
import { X, Sparkles, Image as ImageIcon, Palette, Save, Check, Eye, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/Card';
import Button from '../../../ui/Button';
import { LoadingSpinner } from '../../../ui/Loading';
import Input from '../../../ui/Input';
import { useCreateVfxSchedule } from '../../../../hooks/useApi';
import { CreateVfxScheduleRequest, VfxConfig, BackgroundConfig, VfxType, VfxIntensity } from '../../../../types';
import { APP_THEMES } from '../../../../constants/appThemes';

interface CreateScheduleModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const VFX_TYPES: { value: VfxType; label: string; description: string }[] = [
  { value: 'snow', label: 'Snow', description: 'Gentle falling snowflakes' },
  { value: 'confetti', label: 'Confetti', description: 'Colorful confetti pieces' },
  { value: 'fireworks', label: 'Fireworks', description: 'Exploding fireworks' },
  { value: 'bubbles', label: 'Bubbles', description: 'Rising bubbles' },
  { value: 'stars', label: 'Stars', description: 'Twinkling stars' },
  { value: 'particles_connect', label: 'Particles Connect', description: 'Connected particles forming a network' },
  { value: 'absorbers', label: 'Absorbers', description: 'Particles being absorbed into points' },
  { value: 'polygons', label: 'Polygons', description: 'Flying polygon shapes' },
  { value: 'emitters', label: 'Emitters', description: 'Continuously emitting particles' },
  { value: 'sparkles', label: 'Sparkles', description: 'Sparkling effect' },
];

const INTENSITY_OPTIONS: { value: VfxIntensity; label: string; description: string }[] = [
  { value: 'light', label: 'Light', description: '50% particles (subtle)' },
  { value: 'medium', label: 'Medium', description: '100% particles (balanced)' },
  { value: 'heavy', label: 'Heavy', description: '150% particles (prominent)' },
];

const THEME_NAMES: Record<number, { name: string; description: string }> = {
  1: { name: 'Christmas Magic', description: 'Santa, reindeer, and festive decorations' },
  2: { name: 'New Year Celebration', description: 'Champagne, fireworks, and party vibes' },
  3: { name: 'Valentine Romance', description: 'Hearts, roses, and love in the air' },
  4: { name: 'St Patrick Luck', description: 'Shamrocks, gold coins, and Irish charm' },
  5: { name: 'Spring Bloom', description: 'Flowers, butterflies, and spring awakening' },
  6: { name: 'Summer Vibes', description: 'Beach, sun, and tropical paradise' },
  7: { name: 'Independence Day', description: 'American flag, fireworks, and patriotic spirit' },
  8: { name: 'Halloween Spooky', description: 'Pumpkins, ghosts, and spooky fun' },
  9: { name: 'Thanksgiving Harvest', description: 'Autumn leaves, pumpkins, and harvest bounty' },
  10: { name: 'Winter Wonderland', description: 'Snowman, snowflakes, and cozy winter' },
};

const THEMES = Array.from({ length: 10 }, (_, i) => i + 1);

const CreateScheduleModal: React.FC<CreateScheduleModalProps> = ({ onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState<'basic' | 'vfx' | 'background' | 'appTheme'>('basic');
  const [formData, setFormData] = useState({
    name: '',
    start_time: '',
    end_time: '',
    is_active: true,
  });
  const [vfxConfig, setVfxConfig] = useState<VfxConfig>({
    enabled: false,
    type: null,
    intensity: null,
  });
  const [backgroundConfig, setBackgroundConfig] = useState<BackgroundConfig>({
    enabled: false,
    url: null,
    fallback: '#a1a1d4',
    theme: 0,
  });
  const [appTheme, setAppTheme] = useState<number | null>(null);
  const [previewTheme, setPreviewTheme] = useState<number | null>(null);
  const [previewAppTheme, setPreviewAppTheme] = useState<number | null>(null);

  const createSchedule = useCreateVfxSchedule();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.start_time || !formData.end_time) {
      return;
    }

    if (new Date(formData.end_time) < new Date(formData.start_time)) {
      return;
    }

    const requestData: CreateVfxScheduleRequest = {
      name: formData.name,
      start_time: new Date(formData.start_time).toISOString(),
      end_time: new Date(formData.end_time).toISOString(),
      is_active: formData.is_active,
      vfx: {
        enabled: vfxConfig.enabled,
        ...(vfxConfig.enabled && {
          type: vfxConfig.type!,
          intensity: vfxConfig.intensity!,
        }),
      },
      background: {
        enabled: backgroundConfig.enabled,
        fallback: backgroundConfig.fallback,
        theme: backgroundConfig.theme || 0,
      },
      app_themes: appTheme,
    };

    try {
      await createSchedule.mutateAsync(requestData);
      onSuccess();
      onClose();
    } catch (error) {
      // Error handled by hook
    }
  };

  const getVfxCompatibleBadge = (compatible: string) => {
    if (compatible === 'excellent') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
          <Star className="h-3 w-3 mr-1" />
          Excellent
        </span>
      );
    } else if (compatible === 'good') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
          Good
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
        Fair
      </span>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 overflow-y-auto !m-0 !mt-0 p-4 sm:p-6">
      <div className="min-h-full flex items-start justify-center">
        <div className="relative w-full max-w-4xl my-4 sm:my-8" onClick={(e) => e.stopPropagation()}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-xl font-semibold">Create Schedule</CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Tabs Navigation */}
              <div className="border-b border-gray-200">
                <nav className="flex space-x-1" aria-label="Tabs">
                  <button
                    type="button"
                    onClick={() => setActiveTab('basic')}
                    className={`
                      flex items-center space-x-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors
                      ${
                        activeTab === 'basic'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                      }
                    `}
                  >
                    <span>Basic Info</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('vfx')}
                    className={`
                      flex items-center space-x-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors
                      ${
                        activeTab === 'vfx'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                      }
                    `}
                  >
                    <Sparkles className="h-4 w-4" />
                    <span>VFX</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('background')}
                    className={`
                      flex items-center space-x-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors
                      ${
                        activeTab === 'background'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                      }
                    `}
                  >
                    <ImageIcon className="h-4 w-4" />
                    <span>Background</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('appTheme')}
                    className={`
                      flex items-center space-x-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors
                      ${
                        activeTab === 'appTheme'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                      }
                    `}
                  >
                    <Palette className="h-4 w-4" />
                    <span>App Theme</span>
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="min-h-[400px]">
                {activeTab === 'basic' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Schedule Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter schedule name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Start Time <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="datetime-local"
                        value={formData.start_time}
                        onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        End Time <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="datetime-local"
                        value={formData.end_time}
                        onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                        min={formData.start_time}
                        required
                      />
                      {formData.end_time && formData.start_time && new Date(formData.end_time) < new Date(formData.start_time) && (
                        <p className="text-xs text-red-500 mt-1">End time must be after start time</p>
                      )}
                    </div>

                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="h-4 w-4"
                      />
                      <label htmlFor="is_active" className="text-sm font-medium">
                        Active
                      </label>
                    </div>
                  </div>
                )}

                {activeTab === 'vfx' && (
                  <div className="space-y-6">
                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <label className="text-sm font-medium">Enable VFX Effects</label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Toggle to enable or disable visual effects on client websites
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={vfxConfig.enabled}
                          onChange={(e) => {
                            setVfxConfig({
                              enabled: e.target.checked,
                              type: e.target.checked ? (vfxConfig.type || 'snow') : null,
                              intensity: e.target.checked ? (vfxConfig.intensity || 'medium') : null,
                            });
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {vfxConfig.enabled && (
                      <>
                        {/* VFX Type Selection */}
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            VFX Type <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={vfxConfig.type || ''}
                            onChange={(e) => setVfxConfig({ ...vfxConfig, type: e.target.value as VfxType })}
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          >
                            <option value="">Select a VFX type</option>
                            {VFX_TYPES.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label} - {type.description}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Intensity Selection */}
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Intensity <span className="text-red-500">*</span>
                          </label>
                          <div className="grid grid-cols-3 gap-3">
                            {INTENSITY_OPTIONS.map((option) => (
                              <label
                                key={option.value}
                                className={`flex flex-col p-3 border rounded-lg cursor-pointer transition-colors ${
                                  vfxConfig.intensity === option.value
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="intensity"
                                  value={option.value}
                                  checked={vfxConfig.intensity === option.value}
                                  onChange={(e) => setVfxConfig({ ...vfxConfig, intensity: e.target.value as VfxIntensity })}
                                  className="sr-only"
                                  required
                                />
                                <span className="font-medium text-sm">{option.label}</span>
                                <span className="text-xs text-muted-foreground mt-1">{option.description}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {activeTab === 'background' && (
                  <div className="space-y-6">
                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <label className="text-sm font-medium">Enable Background Image</label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Toggle to enable or disable background image on client websites
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={backgroundConfig.enabled}
                          onChange={(e) => {
                            setBackgroundConfig({
                              ...backgroundConfig,
                              enabled: e.target.checked,
                              theme: e.target.checked ? (backgroundConfig.theme || 0) : 0,
                            });
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {backgroundConfig.enabled && (
                      <>
                        {/* Theme Selection */}
                        <div>
                          <label className="block text-sm font-medium mb-3">
                            Select Theme
                          </label>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                            {THEMES.map((themeNum) => {
                              const isSelected = backgroundConfig.theme === themeNum;
                              return (
                                <div
                                  key={themeNum}
                                  className={`relative group border-2 rounded-lg overflow-hidden transition-all cursor-pointer ${
                                    isSelected
                                      ? 'border-blue-500 ring-2 ring-blue-200'
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                  onClick={() => setBackgroundConfig({ ...backgroundConfig, theme: backgroundConfig.theme === themeNum ? 0 : themeNum })}
                                >
                                  <div className="aspect-video bg-gray-100 relative">
                                    <img
                                      src={`/imgs/theme${themeNum}.png`}
                                      alt={`Theme ${themeNum}`}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none';
                                      }}
                                    />
                                    {isSelected && (
                                      <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center pointer-events-none">
                                        <Check className="h-6 w-6 text-blue-600" />
                                      </div>
                                    )}
                                    <div className="absolute top-2 right-2 z-30">
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          setPreviewTheme(themeNum);
                                        }}
                                        className="bg-white/90 hover:bg-white shadow-md"
                                      >
                                        <Eye className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="p-2 bg-white">
                                    <p className="text-xs font-medium text-center">{THEME_NAMES[themeNum]?.name || `Theme ${themeNum}`}</p>
                                    <p className="text-xs text-muted-foreground text-center mt-0.5 line-clamp-1">
                                      {THEME_NAMES[themeNum]?.description || ''}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {activeTab === 'appTheme' && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium mb-3">
                        Select App Theme
                      </label>
                      <p className="text-xs text-muted-foreground mb-4">
                        Choose a theme for the application interface. Themes affect the overall color scheme and gradient backgrounds.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {APP_THEMES.map((theme) => {
                          const isSelected = appTheme === theme.id;
                          return (
                            <div
                              key={theme.id}
                              className={`relative group border-2 rounded-lg overflow-hidden transition-all cursor-pointer ${
                                isSelected
                                  ? 'border-blue-500 ring-2 ring-blue-200 shadow-lg'
                                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                              }`}
                              onClick={() => setAppTheme(appTheme === theme.id ? null : theme.id)}
                            >
                              <div className={`aspect-video ${theme.gradient} relative`}>
                                {isSelected && (
                                  <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center pointer-events-none">
                                    <Check className="h-8 w-8 text-blue-600" />
                                  </div>
                                )}
                                <div className="absolute top-2 right-2 z-30">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      setPreviewAppTheme(theme.id);
                                    }}
                                    className="bg-white/90 hover:bg-white shadow-md"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                              <div className="p-3 bg-white">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1">
                                    <p className="text-sm font-semibold">{theme.name}</p>
                                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                      {theme.description}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between mt-2">
                                  {getVfxCompatibleBadge(theme.vfxCompatible)}
                                  <span className="text-xs text-muted-foreground">
                                    #{theme.id}
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2 line-clamp-1">
                                  {theme.bestFor}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={onClose} disabled={createSchedule.isLoading}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createSchedule.isLoading || !formData.name || !formData.start_time || !formData.end_time}
                  className="flex items-center space-x-2"
                >
                  {createSchedule.isLoading ? (
                    <>
                      <LoadingSpinner />
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Create Schedule</span>
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        </div>
      </div>

      {/* Preview Modals */}
      {previewTheme !== null && (
        <div
          className="fixed inset-0 z-[60] bg-black bg-opacity-75 overflow-y-auto overscroll-contain !m-0 !mt-0 p-4 sm:p-6"
          onClick={() => setPreviewTheme(null)}
        >
          <div className="min-h-full flex items-start justify-center">
            <div className="relative w-full max-w-4xl my-4 sm:my-8" onClick={(e) => e.stopPropagation()}>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <div>
                  <CardTitle className="text-xl font-semibold">
                    {THEME_NAMES[previewTheme]?.name || `Theme ${previewTheme}`}
                  </CardTitle>
                  {THEME_NAMES[previewTheme]?.description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {THEME_NAMES[previewTheme].description}
                    </p>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setPreviewTheme(null)}>
                  <X className="h-5 w-5" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="relative w-full rounded-lg border overflow-hidden bg-gray-100" style={{ aspectRatio: '16/9' }}>
                  <img
                    src={`/imgs/theme${previewTheme}.png`}
                    alt={`${THEME_NAMES[previewTheme]?.name || `Theme ${previewTheme}`} preview`}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
                <div className="flex justify-end mt-4 space-x-2">
                  <Button variant="outline" onClick={() => setPreviewTheme(null)}>
                    Close
                  </Button>
                </div>
              </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {previewAppTheme !== null && (
        <div
          className="fixed inset-0 z-[60] bg-black bg-opacity-75 overflow-y-auto overscroll-contain !m-0 !mt-0 p-4 sm:p-6"
          onClick={() => setPreviewAppTheme(null)}
        >
          <div className="min-h-full flex items-start justify-center">
            <div className="relative w-full max-w-4xl my-4 sm:my-8" onClick={(e) => e.stopPropagation()}>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <div>
                  <CardTitle className="text-xl font-semibold">
                    {APP_THEMES.find(t => t.id === previewAppTheme)?.name || `Theme ${previewAppTheme}`}
                  </CardTitle>
                  {APP_THEMES.find(t => t.id === previewAppTheme)?.description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {APP_THEMES.find(t => t.id === previewAppTheme)?.description}
                    </p>
                  )}
                  <div className="mt-2">
                    {APP_THEMES.find(t => t.id === previewAppTheme) && getVfxCompatibleBadge(APP_THEMES.find(t => t.id === previewAppTheme)!.vfxCompatible)}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setPreviewAppTheme(null)}>
                  <X className="h-5 w-5" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className={`relative w-full rounded-lg border overflow-hidden ${APP_THEMES.find(t => t.id === previewAppTheme)?.gradient || ''}`} style={{ aspectRatio: '16/9' }}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-white/30 text-2xl font-medium">Theme Preview</span>
                  </div>
                </div>
                {APP_THEMES.find(t => t.id === previewAppTheme) && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium mb-2">Best For:</p>
                    <p className="text-sm text-muted-foreground">
                      {APP_THEMES.find(t => t.id === previewAppTheme)?.bestFor}
                    </p>
                  </div>
                )}
                <div className="flex justify-end mt-4 space-x-2">
                  <Button variant="outline" onClick={() => setPreviewAppTheme(null)}>
                    Close
                  </Button>
                </div>
              </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateScheduleModal;
